import { ConfigSearchEntry } from './searchIndex'

const TICK_MS = 150
/** Long enough for a section to load its data before the first tab is tried. */
const SETTLE_TICKS = 6
const MAX_TICKS = 70
const HIGHLIGHT_MS = 2600

const norm = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase()

/**
 * The `<a>` elements of a `ButtonGroup` sub-tab strip inside the section.
 *
 * Deliberately narrow: anything else on the page that happens to carry the
 * searched-for text must not be clicked. If the ButtonGroup markup ever
 * changes this simply finds nothing and the jump stops at the section.
 */
const tabAnchors = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>('a')).filter((anchor) => {
    const parent = anchor.parentElement
    if (!parent || parent.querySelectorAll(':scope > a').length < 2) return false
    const cls = parent.className || ''
    return typeof cls === 'string' && cls.includes('rounded-xl') && cls.includes('bg-slate-100')
  })

/** Select the sub-tab with this name. Returns false when it is missing or already open. */
const openTab = (root: HTMLElement, name: string): boolean => {
  const match = tabAnchors(root).find((anchor) => norm(anchor.textContent || '') === norm(name))
  if (!match || match.getAttribute('aria-current') === 'page') return false
  match.click()
  return true
}

/** Name of the sub-tab currently showing, if the section has a tab strip. */
const activeTab = (root: HTMLElement): string | null => {
  const current = tabAnchors(root).find((anchor) => anchor.getAttribute('aria-current') === 'page')
  return current ? (current.textContent || '').trim() : null
}

/** The visible element carrying this label, preferring an exact text match. */
const findLabel = (root: HTMLElement, label: string): HTMLElement | null => {
  const target = norm(label)
  if (!target) return null
  let partial: HTMLElement | null = null
  const nodes = root.querySelectorAll<HTMLElement>('label, span, div, p, h1, h2, h3, h4, th, td, a, button')
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i]
    if (el.children.length) continue // leaf text nodes only
    const text = norm(el.textContent || '')
    if (!text) continue
    if (text === target) return el
    if (!partial && text.includes(target) && text.length < target.length + 40) partial = el
  }
  return partial
}

/** Grow up from a bare text node to something big enough to be worth ringing. */
const highlightHost = (el: HTMLElement): HTMLElement => {
  let host = el
  for (let i = 0; i < 3; i += 1) {
    if (host.offsetWidth > 160 && host.offsetHeight > 20) break
    if (!host.parentElement) break
    host = host.parentElement
  }
  return host
}

/**
 * Switch to the searched-for setting inside the already-rendered section: open
 * its sub-tab if it has one, scroll it into view and flash a ring around it.
 *
 * Sections load their data asynchronously, so this retries for a few seconds
 * and then gives up quietly - the section itself is already open either way.
 */
export const revealConfigTarget = (getRoot: () => HTMLElement | null, entry: ConfigSearchEntry) => {
  let ticks = 0
  let openedSection = false
  let openedLabelTab = false
  let timer: ReturnType<typeof setInterval> | null = null
  let clearHighlight: ReturnType<typeof setTimeout> | null = null

  // Sweep state: the section's own sub-tabs, tried one per tick as a last resort.
  let sweep: string[] | null = null
  let sweepIndex = 0
  let originalTab: string | null = null

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }

  const flash = (el: HTMLElement) => {
    const host = highlightHost(el)
    const previousShadow = host.style.boxShadow
    const previousRadius = host.style.borderRadius
    const brand = getComputedStyle(document.documentElement).getPropertyValue('--color-shade-blue').trim() || '0 76 142'
    host.scrollIntoView({ block: 'center', behavior: 'smooth' })
    host.style.boxShadow = `0 0 0 2px rgb(${brand})`
    host.style.borderRadius = '6px'
    clearHighlight = setTimeout(() => {
      host.style.boxShadow = previousShadow
      host.style.borderRadius = previousRadius
    }, HIGHLIGHT_MS)
  }

  timer = setInterval(() => {
    ticks += 1
    const root = getRoot()
    if (!root) {
      if (ticks >= MAX_TICKS) stop()
      return
    }

    // The entry's own sub-tab first, then the label itself when the label is a tab.
    if (!openedSection && entry.section && entry.section !== 'Tab' && openTab(root, entry.section)) {
      openedSection = true
      return
    }
    if (!openedLabelTab && openTab(root, entry.label)) {
      openedLabelTab = true
      return
    }

    const el = findLabel(root, entry.label)
    if (el) {
      flash(el)
      stop()
      return
    }

    // Not on the tab that is showing. Walk the section's other sub-tabs, and put
    // the tab the user was on back if the setting turns up on none of them.
    if (ticks < SETTLE_TICKS) return
    if (sweep === null) {
      originalTab = activeTab(root)
      sweep = tabAnchors(root)
        .map((anchor) => (anchor.textContent || '').trim())
        .filter((name) => name && name !== originalTab)
    }
    if (sweepIndex < sweep.length) {
      openTab(root, sweep[sweepIndex])
      sweepIndex += 1
      return
    }
    if (originalTab) openTab(root, originalTab)
    stop()
  }, TICK_MS)

  return () => {
    stop()
    if (clearHighlight) clearTimeout(clearHighlight)
  }
}
